// Management review register (ISO 9001 clause 9.3). Office/admin. A filterable list of
// management reviews with a New review modal and a per-row View/edit modal (which also moves
// the status forward and deletes/archives). Self-contained: no separate detail route.
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import {
  api,
  ApiError,
  type ManagementReviewListItem,
  type ManagementReviewDetail,
  type ManagementReviewStatus,
  type ManagementReviewCreateInput,
} from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, ModalShell } from "../../components/ModalShell";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { SortableHeader } from "../../components/SortableHeader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";

const STATUS_LABEL: Record<ManagementReviewStatus, string> = { draft: "Draft", finalised: "Finalised" };

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "finalised", label: "Finalised" },
];

const inputCls = "rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

// Client-side search and sort over the rows the (unchanged) server status filter returned. The search
// term is the URL's ?q=; it matches only what the table shows.
const REVIEW_SEARCH: SearchAccessors<ManagementReviewListItem> = [
  (r) => r.reviewRef,
  (r) => r.attendees,
  (r) => STATUS_LABEL[r.status] ?? r.status,
];
const REVIEW_STATUS_ORDER: Record<string, number> = { draft: 0, finalised: 1 };
const REVIEW_SORT: SortAccessors<ManagementReviewListItem> = {
  ref: (r) => r.reviewRef,
  reviewDate: (r) => {
    const t = Date.parse(r.reviewDate);
    return Number.isNaN(t) ? null : t;
  },
  attendees: (r) => (r.attendees ?? "").toLowerCase(),
  status: (r) => REVIEW_STATUS_ORDER[r.status] ?? null,
  nextReview: (r) => {
    const t = r.nextReviewDate ? Date.parse(r.nextReviewDate) : NaN;
    return Number.isNaN(t) ? null : t;
  },
};
const SORT_OPTIONS = [
  { key: "ref", label: "Reference" },
  { key: "reviewDate", label: "Review date" },
  { key: "attendees", label: "Attendees" },
  { key: "status", label: "Status" },
  { key: "nextReview", label: "Next review" },
];

export function ManagementReviewsPage() {
  const [rows, setRows] = useState<ManagementReviewListItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(() => {
    setLoadError(false);
    api.managementReviews
      .list((status || "all") as ManagementReviewStatus | "all")
      .then(setRows)
      .catch(() => setLoadError(true));
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("management-reviews");
  const shown = rows ? sortRows(filterRows(rows, search, REVIEW_SEARCH), REVIEW_SORT, sort) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        title="Management reviews"
        className="mb-4"
        subtitle="Management review meetings, inputs, decisions and actions (ISO 9001 clause 9.3)."
        actions={
          <Button onClick={() => setAdding(true)} icon={<Plus aria-hidden size={15} />}>
            New review
          </Button>
        }
      />

      <ListToolbar
        className="mb-4"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search reference or attendees..."
        searchLabel="Search management reviews"
        resultCount={rows ? { shown: shown?.length ?? 0, total: rows.length, noun: "reviews" } : undefined}
        sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
        onClear={() => setSearch("")}
        filters={
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value || "all"}
                onClick={() => setStatus(f.value)}
                className={`rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${
                  status === f.value ? "border-ele-green bg-ele-inset text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-green"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        }
      />

      {loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load management reviews. Please try again.
        </p>
      ) : rows === null || shown === null ? (
        <Loader label="Loading management reviews..." />
      ) : rows.length === 0 ? (
        <EmptyState message="No management reviews recorded." />
      ) : shown.length === 0 ? (
        <EmptyState role="status" message={`No management reviews match "${search.trim()}".`} />
      ) : mobileCards ? (
        // Attendees and Next review are desktop-only below md, leaving Reference/Review
        // date/Status to divide a phone width. The card restores Attendees as the second line
        // and Next review to the details block, mirroring the Invoices list precedent.
        <MobileCardList label="Management reviews">
          {shown.map((r) => (
            <MobileListCard
              key={r.id}
              onClick={() => setEditId(r.id)}
              primary={r.reviewRef}
              secondary={r.attendees}
              meta={`Reviewed ${formatDate(r.reviewDate)}`}
              right={<StatusPill domain="managementReview" status={r.status} />}
              details={r.nextReviewDate ? <p className="text-xs text-ele-body">Next review: {formatDate(r.nextReviewDate)}</p> : undefined}
            />
          ))}
        </MobileCardList>
      ) : (
        <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
          <table className="ele-list-table w-full text-left text-sm">
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "16%" }} />
              <col className="max-md:hidden" style={{ width: "36%" }} />
              <col style={{ width: "14%" }} />
              <col className="max-md:hidden" style={{ width: "14%" }} />
              <col className="w-8 md:hidden" />
            </colgroup>
            <thead className="bg-ele-inset font-menu text-xs uppercase tracking-wide text-ele-blue">
              <tr>
                <SortableHeader label="Reference" colKey="ref" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Review date" colKey="reviewDate" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Attendees" colKey="attendees" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
                <SortableHeader label="Next review" colKey="nextReview" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
                <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id} onClick={() => setEditId(r.id)} className="group cursor-pointer border-t border-ele-hairline hover:bg-ele-surface">
                  <td className="px-3 py-2 font-semibold text-ele-strong">{r.reviewRef}</td>
                  <td className="px-3 py-2 text-ele-body">{formatDate(r.reviewDate)}</td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{r.attendees}</td>
                  <td className="px-3 py-2">
                    <StatusPill domain="managementReview" status={r.status} />
                  </td>
                  <td className="max-md:hidden px-3 py-2 text-ele-body">{formatDate(r.nextReviewDate)}</td>
                  <td className="cell-actions md:hidden pr-1 text-right">
                    <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                      <path d="M6 4l4 4-4 4z" />
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <ReviewModal
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            load();
          }}
        />
      )}
      {editId && (
        <ReviewModal
          id={editId}
          onClose={() => setEditId(null)}
          onSaved={() => {
            setEditId(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function ReviewModal({ id, onClose, onSaved }: { id?: string; onClose: () => void; onSaved: () => void }) {
  const editing = id != null;
  const [form, setForm] = useState({
    reviewDate: "",
    attendees: "",
    inputSummary: "",
    decisions: "",
    actionsRequired: "",
    actionsDueDate: "",
    nextReviewDate: "",
    status: "draft" as ManagementReviewStatus,
  });
  const [allowedNext, setAllowedNext] = useState<ManagementReviewStatus[]>([]);
  const [loading, setLoading] = useState(editing);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.managementReviews
      .get(id)
      .then((d: ManagementReviewDetail) => {
        setForm({
          reviewDate: d.reviewDate ? d.reviewDate.slice(0, 10) : "",
          attendees: d.attendees,
          inputSummary: d.inputSummary,
          decisions: d.decisions,
          actionsRequired: d.actionsRequired ?? "",
          actionsDueDate: d.actionsDueDate ? d.actionsDueDate.slice(0, 10) : "",
          nextReviewDate: d.nextReviewDate ? d.nextReviewDate.slice(0, 10) : "",
          status: d.status,
        });
        setAllowedNext(d.allowedNextStatuses);
      })
      .catch(() => setErr("Could not load this review."))
      .finally(() => setLoading(false));
  }, [id]);

  const set =
    (name: keyof typeof form) =>
    (e: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [name]: e.target.value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form.reviewDate || !form.attendees.trim() || !form.inputSummary.trim() || !form.decisions.trim()) {
      setErr("Review date, attendees, inputs and decisions are required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (editing && id) {
        await api.managementReviews.update(id, {
          reviewDate: form.reviewDate,
          attendees: form.attendees.trim(),
          inputSummary: form.inputSummary.trim(),
          decisions: form.decisions.trim(),
          actionsRequired: form.actionsRequired.trim() || null,
          actionsDueDate: form.actionsDueDate || null,
          nextReviewDate: form.nextReviewDate || null,
          status: form.status,
        });
      } else {
        const body: ManagementReviewCreateInput = {
          reviewDate: form.reviewDate,
          attendees: form.attendees.trim(),
          inputSummary: form.inputSummary.trim(),
          decisions: form.decisions.trim(),
          actionsRequired: form.actionsRequired.trim() || null,
          actionsDueDate: form.actionsDueDate || null,
          nextReviewDate: form.nextReviewDate || null,
        };
        await api.managementReviews.create(body);
      }
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not save the review. Please try again.");
      setBusy(false);
    }
  }

  async function remove() {
    if (!id) return;
    setBusy(true);
    setErr(null);
    try {
      await api.managementReviews.remove(id);
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not delete the review. Please try again.");
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  const statusOptions: ManagementReviewStatus[] = editing ? [form.status, ...allowedNext] : ["draft"];

  return (
    <ModalShell ariaLabel={editing ? "Edit review" : "New review"} onClose={() => !busy && !confirmingDelete && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <form onSubmit={save}>
        <h2 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">{editing ? "Management review" : "New management review"}</h2>
        {loading ? (
          <Loader label="Loading this review..." shape="panel" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <label>
              <span className={labelCls}>Review date</span>
              <input aria-label="Review date" type="date" value={form.reviewDate} onChange={set("reviewDate")} className={`${inputCls} w-full`} />
            </label>
            <label>
              <span className={labelCls}>Next review date</span>
              <input aria-label="Next review date" type="date" value={form.nextReviewDate} onChange={set("nextReviewDate")} className={`${inputCls} w-full`} />
            </label>
            <label className="sm:col-span-2">
              <span className={labelCls}>Attendees</span>
              <input aria-label="Attendees" value={form.attendees} onChange={set("attendees")} placeholder="e.g. Director, Quality Lead" className={`${inputCls} w-full`} />
            </label>
            <label className="sm:col-span-2">
              <span className={labelCls}>Inputs reviewed</span>
              <textarea aria-label="Inputs reviewed" value={form.inputSummary} onChange={set("inputSummary")} rows={3} placeholder="Customer feedback, audit results, process performance, etc." className={`${inputCls} w-full`} />
            </label>
            <label className="sm:col-span-2">
              <span className={labelCls}>Decisions</span>
              <textarea aria-label="Decisions" value={form.decisions} onChange={set("decisions")} rows={3} className={`${inputCls} w-full`} />
            </label>
            <label className="sm:col-span-2">
              <span className={labelCls}>Actions required (optional)</span>
              <textarea aria-label="Actions required" value={form.actionsRequired} onChange={set("actionsRequired")} rows={2} className={`${inputCls} w-full`} />
            </label>
            <label>
              <span className={labelCls}>Actions due date</span>
              <input aria-label="Actions due date" type="date" value={form.actionsDueDate} onChange={set("actionsDueDate")} className={`${inputCls} w-full`} />
            </label>
            {editing && (
              <label>
                <span className={labelCls}>Status</span>
                <select aria-label="Status" value={form.status} onChange={set("status")} className={`${inputCls} w-full`}>
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}
        {err && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {err}
          </p>
        )}
        <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            {editing && (
              <Button variant="destructive" size="sm" onClick={() => setConfirmingDelete(true)} disabled={busy} className={MODAL_ACTION_BTN}>
                Delete
              </Button>
            )}
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button type="button" onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
              Cancel
            </button>
            <Button type="submit" disabled={busy || loading} className={MODAL_ACTION_BTN}>
              {busy ? "Saving..." : editing ? "Save changes" : "Create review"}
            </Button>
          </div>
        </div>
      </form>

      <ConfirmDeleteModal
        open={confirmingDelete}
        title="Delete this review?"
        message="A draft review is permanently deleted. A finalised review is a retained quality record, so it is archived (kept on file, hidden from the list) instead."
        busy={busy}
        onConfirm={remove}
        onCancel={() => setConfirmingDelete(false)}
      />
    </ModalShell>
  );
}
